import { Box, Flex, FormLabel, SimpleGrid, Switch, Text } from '@chakra-ui/react';
import { currencyFormat, numberFormat } from 'components/Number';
import ChartTooltip from 'components/charts/ChartTooltip';
import SelectInput from 'components/inputs/SelectInput';
import { generateSelectOptions } from 'helpers/inputHelper';
import PropTypes from 'prop-types';
import { useMemo, useState } from 'react';
import Chart from 'react-apexcharts';
import { renderToString } from 'react-dom/server';
import SalesSectionCard from '../Sales/components/SalesSectionCard';
import { cohortColor } from './cohortColor';
import { visibleCohortForecasts } from './forecast/cacExpandingForecast';
import {
  SPEND_ABOVE_SEARCH_MAX,
  buildCacTrend,
  cohortActualCac,
} from './forecast/cacTrendFit';
import { FIT_TYPE, FIT_TYPE_META } from './forecast/fitCurve';

const FIT_COLOR = '#2B6CB0';
const OTHER_FIT_COLOR = '#A0AEC0';
const ONE_SIGMA_FILL = '#63B3ED';
const TWO_SIGMA_FILL = '#BEE3F8';

function curveSeries(samples, value) {
  return samples
    .map((sample) => [sample.spend, sample[value]])
    .filter((point) => Number.isFinite(point[1]));
}

function rangeSeries(samples, lowKey, highKey) {
  return samples
    .filter((sample) => Number.isFinite(sample[lowKey]) && Number.isFinite(sample[highKey]))
    .map((sample) => [sample.spend, [sample[lowKey], sample[highKey]]]);
}

const CacVsMarketingChart = ({ cohorts, selectedIds, maxCac }) => {
  const [fitType, setFitType] = useState(FIT_TYPE.LINEAR);
  const [showOtherFits, setShowOtherFits] = useState(false);
  const [showBands, setShowBands] = useState(true);
  const [showMaxCac, setShowMaxCac] = useState(true);

  const selected = useMemo(() => new Set(selectedIds || []), [selectedIds]);
  const trend = useMemo(
    () => buildCacTrend({ cohorts, selectedIds, fitType, maxCac }),
    [cohorts, selectedIds, fitType, maxCac]
  );
  const forecasts = useMemo(
    () => visibleCohortForecasts(cohorts, selectedIds),
    [cohorts, selectedIds]
  );

  const chart = useMemo(() => {
    const series = [];
    (cohorts || []).forEach((cohort, index) => {
      const cac = cohortActualCac(cohort);
      if (!selected.has(cohort.id) || !(cohort.uaSpend > 0) || !(cac > 0)) {
        return;
      }
      series.push({
        name: `${cohort.label} actual`,
        type: 'scatter',
        color: cohortColor(index),
        data: [[cohort.uaSpend, cac]],
        showMarkers: true,
      });
    });
    forecasts.forEach((cohort) => {
      const index = (cohorts || []).findIndex((item) => item.id === cohort.id);
      series.push({
        name: `${cohort.label} forecast`,
        type: 'scatter',
        color: cohortColor(index),
        data: [[cohort.uaSpend, cohort.forecastCac]],
        showMarkers: true,
        hollow: true,
      });
    });

    if (trend.fit) {
      if (showOtherFits) {
        Object.values(FIT_TYPE)
          .filter((type) => type !== fitType)
          .forEach((type) => {
            const other = buildCacTrend({ cohorts, selectedIds, fitType: type });
            if (!other.fit) {
              return;
            }
            series.push({
              name: FIT_TYPE_META[type].label,
              type: 'line',
              color: OTHER_FIT_COLOR,
              dashArray: 4,
              data: curveSeries(other.samples, 'fitted'),
            });
          });
      }
      if (showBands && trend.sigma > 0) {
        series.push(
          {
            name: '±2σ',
            type: 'rangeArea',
            color: TWO_SIGMA_FILL,
            fillOpacity: 0.85,
            data: rangeSeries(trend.samples, 'low2', 'high2'),
          },
          {
            name: '±1σ',
            type: 'rangeArea',
            color: ONE_SIGMA_FILL,
            fillOpacity: 0.45,
            data: rangeSeries(trend.samples, 'low1', 'high1'),
          },
          {
            name: '+2σ',
            type: 'line',
            color: FIT_COLOR,
            dashArray: 2,
            strokeWidth: 1,
            data: curveSeries(trend.samples, 'high2'),
          },
          {
            name: '−2σ',
            type: 'line',
            color: FIT_COLOR,
            dashArray: 2,
            strokeWidth: 1,
            data: curveSeries(trend.samples, 'low2'),
          }
        );
      }
      series.push({
        name: FIT_TYPE_META[fitType].label,
        type: 'line',
        color: FIT_COLOR,
        data: curveSeries(trend.samples, 'fitted'),
      });
    }

    return { series };
  }, [cohorts, forecasts, selected, selectedIds, fitType, showOtherFits, showBands, trend]);

  const options = useMemo(() => {
    return {
      chart: {
        type: 'line',
        fontFamily: 'DM Sans',
        animations: { enabled: false },
        toolbar: { show: false },
        zoom: { enabled: false },
      },
      colors: chart.series.map((item) => item.color),
      stroke: {
        width: chart.series.map((item) => {
          if (item.strokeWidth != null) {
            return item.strokeWidth;
          }
          return item.type === 'line' ? 2 : 0;
        }),
        dashArray: chart.series.map((item) => item.dashArray || 0),
        curve: 'straight',
      },
      fill: {
        type: 'solid',
        opacity: chart.series.map((item) => item.fillOpacity ?? 1),
      },
      markers: {
        size: chart.series.map((item) => (item.showMarkers ? 6 : 0)),
        colors: chart.series.map((item) => (item.hollow ? '#ffffff' : item.color)),
        strokeColors: chart.series.map((item) => item.color),
        strokeWidth: 2,
        hover: { sizeOffset: 2 },
      },
      dataLabels: { enabled: false },
      legend: { show: false },
      annotations:
        showMaxCac && maxCac > 0
          ? {
              yaxis: [
                {
                  y: maxCac,
                  borderColor: '#E53E3E',
                  strokeDashArray: 6,
                  label: {
                    text: `Max CAC ${currencyFormat(maxCac)}`,
                    style: { color: '#E53E3E', background: '#ffffff' },
                  },
                },
              ],
            }
          : {},
      tooltip: {
        shared: false,
        intersect: true,
        custom: ({ seriesIndex, dataPointIndex, w }) => {
          const pointSeries = w.config.series[seriesIndex];
          if (!pointSeries || pointSeries.type !== 'scatter') {
            return '';
          }
          const point = pointSeries.data[dataPointIndex];
          if (!Array.isArray(point) || Array.isArray(point[1])) {
            return '';
          }
          return renderToString(
            <ChartTooltip
              title={pointSeries.name}
              hideMarkers
              items={[
                {
                  label: pointSeries.hollow ? 'Predicted CAC' : 'CAC',
                  value: currencyFormat(point[1], { maximumFractionDigits: 1 }),
                },
                { label: 'UA', value: currencyFormat(point[0]) },
              ]}
            />
          );
        },
      },
      xaxis: {
        type: 'numeric',
        title: { text: 'Cohort UA spend' },
        labels: { formatter: (value) => currencyFormat(value) },
        tooltip: { enabled: false },
      },
      yaxis: {
        title: { text: 'CAC' },
        labels: { formatter: (value) => currencyFormat(value) },
      },
    };
  }, [chart.series, maxCac, showMaxCac]);

  const sigmaText =
    trend.sigma == null
      ? '—'
      : fitType === FIT_TYPE.POWER
        ? `${numberFormat(trend.sigma * 100, { maximumFractionDigits: 1 })}% (log)`
        : currencyFormat(trend.sigma, { maximumFractionDigits: 1 });
  const latestText =
    trend.latest == null
      ? ''
      : ` · ${trend.latest.label} ${trend.latest.z > 0 ? '+' : ''}${numberFormat(trend.latest.z, {
          maximumFractionDigits: 1,
        })}σ vs fit`;
  const spendText =
    trend.spendAtMaxCac === SPEND_ABOVE_SEARCH_MAX
      ? '>$6m'
      : trend.spendAtMaxCac == null
        ? '—'
        : currencyFormat(trend.spendAtMaxCac);

  return (
    <SalesSectionCard title="UA spend vs CAC by cohort">
      <Flex gap={4} wrap="wrap" align="center" mt={4}>
        <Flex align="center" gap={2}>
          <FormLabel m={0}>Fit</FormLabel>
          <SelectInput
            options={generateSelectOptions({ metaObj: FIT_TYPE_META })}
            value={fitType}
            onChange={setFitType}
            w="160px"
          />
        </Flex>
        <Flex align="center" gap={2}>
          <Switch
            isChecked={showOtherFits}
            onChange={(event) => setShowOtherFits(event.target.checked)}
          />
          <Text fontSize="sm">Show other fits</Text>
        </Flex>
        <Flex align="center" gap={2}>
          <Switch isChecked={showBands} onChange={(event) => setShowBands(event.target.checked)} />
          <Text fontSize="sm">±1σ / ±2σ bands</Text>
        </Flex>
        <Flex align="center" gap={2}>
          <Switch
            isChecked={showMaxCac}
            onChange={(event) => setShowMaxCac(event.target.checked)}
          />
          <Text fontSize="sm">Max CAC anchor</Text>
        </Flex>
      </Flex>
      <Text mt={3} fontSize="sm" color="gray.600">
        Filled dots are actual CAC. Hollow dots are the CAC predicted from earlier cohorts.
      </Text>
      {trend.fit ? (
        <SimpleGrid columns={{ base: 1, md: 3 }} spacing={2} mt={3} fontSize="sm">
          <Text>
            <b>{FIT_TYPE_META[fitType].label}</b> n {trend.fit.n} R²{' '}
            {Number.isFinite(trend.r2) ? trend.r2.toFixed(2) : '—'}
          </Text>
          <Text>
            σ {sigmaText}
            {latestText}
          </Text>
          <Text>Spend at max CAC on fit {spendText}</Text>
        </SimpleGrid>
      ) : (
        <Text mt={3} fontSize="sm" color="gray.600">
          Select at least {trend.minimumPoints} actual cohorts to fit
        </Text>
      )}
      <Box h="420px" mt={2}>
        {chart.series.length ? (
          <Chart
            type="line"
            width="100%"
            height="100%"
            series={chart.series}
            options={options}
          />
        ) : (
          <Text color="gray.500" mt={8}>
            Select a cohort with UA spend to plot CAC.
          </Text>
        )}
      </Box>
    </SalesSectionCard>
  );
};

CacVsMarketingChart.propTypes = {
  cohorts: PropTypes.array,
  selectedIds: PropTypes.array,
  maxCac: PropTypes.number,
};

export default CacVsMarketingChart;
